import React, { useState } from 'react';
import { useCanvasStore, ViewMode } from '@/stores/canvasStore';
import { cn } from '@/lib/utils';
import { DockGlyph } from '@/components/toolbar/DockGlyph';
import { Compass, Crosshair } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { toast } from 'sonner';

const viewModes: { mode: ViewMode; label: string; icon: React.ElementType }[] = [
  { mode: 'focus', label: 'Create', icon: Crosshair },
  { mode: 'discover', label: 'Discover', icon: Compass },
];

interface ViewModeSwitcherProps {
  expanded?: boolean;
}

export function ViewModeSwitcher({ expanded = false }: ViewModeSwitcherProps) {
  const viewMode = useCanvasStore((s) => s.viewMode);
  const setViewMode = useCanvasStore((s) => s.setViewMode);
  const isMobile = useIsMobile();
  const [shakeCreate, setShakeCreate] = useState(false);

  // Always compact by default, expand only when parent is hovered
  const isCompact = !expanded;

  const handleModeClick = (mode: ViewMode) => {
    // On mobile, show "Coming soon" for Create mode
    if (isMobile && mode === 'focus') {
      setShakeCreate(true);
      const toastId = toast('Coming soon to Beta', {
        position: 'top-center',
      });
      // Manually dismiss after 1.5 seconds
      setTimeout(() => toast.dismiss(toastId), 1500);
      setTimeout(() => setShakeCreate(false), 500);
      return;
    }
    setViewMode(mode);
  };

  return (
    <div className="flex items-center bg-muted/50 rounded-full p-1 transition-all duration-300">
      {viewModes.map(({ mode, label, icon: Icon }) => (
        <button
          key={mode}
          type="button"
          onClick={() => handleModeClick(mode)}
          onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); }}
          data-dock-item
          onTouchStart={(e) => {
            // Prevent mobile long-press from triggering paste/context menu
            if (isMobile && mode === 'focus') {
              e.preventDefault();
              handleModeClick(mode);
            }
          }}
          className={cn(
            'flex items-center justify-center rounded-full text-sm font-medium transition-all overflow-hidden select-none [-webkit-touch-callout:none]',
            // Collapse eases out gently; expanding keeps the snappier default
            isCompact ? 'px-2 py-1.5 gap-0 [transition-duration:350ms] ease-out' : 'px-3 py-1.5 gap-1.5 duration-300',
            viewMode === mode
              ? 'bg-primary text-primary-foreground shadow-sm'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted',
            // Shake animation for Create on mobile
            isMobile && mode === 'focus' && shakeCreate && 'animate-[shake_0.5s_ease-in-out]'
          )}
        >
          <DockGlyph className="flex-shrink-0">
            <Icon className="h-4 w-4" />
          </DockGlyph>
          <span
            className={cn(
              'transition-all whitespace-nowrap',
              // max-width animates both ways; width snapped, since it can't transition to or from auto
              isCompact ? 'max-w-0 opacity-0 [transition-duration:350ms] ease-out' : 'max-w-[6rem] opacity-100 duration-300'
            )}
          >
            {label}
          </span>
        </button>
      ))}
    </div>
  );
}
